/**
 * Feature Tracker Page
 * AIDLC Administrative Frontend
 *
 * Manage features — grouping entities for backlog items (like Scrum Epics).
 * Left panel: feature list with cards.
 * Right panel: selected feature detail with linked items, progress, and management.
 *
 * Personas: all (no restriction)
 */

import { useState, useEffect, useCallback } from 'react';
import { Card } from '../shared/Card';
import { Button } from '../shared/Button';
import { Modal } from '../shared/Modal';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { EmptyState } from '../shared/EmptyState';
import type { Feature, FeatureWithProgress, FeatureSummary } from '../../types/feature';
import type { BacklogItem } from '../../types/backlog';
import {
  getFeatures,
  getFeature,
  getFeatureSummary,
  createFeature,
  updateFeature,
  deleteFeature,
  addItemsToFeature,
  removeItemsFromFeature,
} from '../../services/featureService';
import { getBacklogItems } from '../../services/backlogService';

const statusConfig: Record<string, { label: string; color: string; bg: string }> = {
  planning: { label: 'Planning', color: 'text-blue-700', bg: 'bg-blue-100' },
  active: { label: 'Active', color: 'text-green-700', bg: 'bg-green-100' },
  completed: { label: 'Completed', color: 'text-gray-700', bg: 'bg-gray-200' },
  cancelled: { label: 'Cancelled', color: 'text-red-700', bg: 'bg-red-100' },
};

const priorityConfig: Record<string, { label: string; color: string; bg: string }> = {
  critical: { label: 'Critical', color: 'text-red-700', bg: 'bg-red-100' },
  high: { label: 'High', color: 'text-orange-700', bg: 'bg-orange-100' },
  medium: { label: 'Medium', color: 'text-yellow-700', bg: 'bg-yellow-100' },
  low: { label: 'Low', color: 'text-gray-600', bg: 'bg-gray-100' },
};

const backlogStatusConfig: Record<string, { label: string; color: string; bg: string }> = {
  new: { label: 'New', color: 'text-purple-700', bg: 'bg-purple-100' },
  open: { label: 'Open', color: 'text-blue-700', bg: 'bg-blue-100' },
  in_progress: { label: 'In Progress', color: 'text-yellow-700', bg: 'bg-yellow-100' },
  review: { label: 'Review', color: 'text-orange-700', bg: 'bg-orange-100' },
  done: { label: 'Done', color: 'text-green-700', bg: 'bg-green-100' },
  closed: { label: 'Closed', color: 'text-gray-600', bg: 'bg-gray-200' },
};

const itemStatusColors: Record<string, string> = {
  new: 'bg-purple-500',
  open: 'bg-blue-500',
  in_progress: 'bg-yellow-500',
  review: 'bg-orange-500',
  done: 'bg-green-500',
  closed: 'bg-gray-400',
};

export function FeatureTrackerPage() {
  const [features, setFeatures] = useState<Feature[]>([]);
  const [summary, setSummary] = useState<FeatureSummary | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<FeatureWithProgress | null>(null);
  const [linkedItems, setLinkedItems] = useState<BacklogItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showAddItems, setShowAddItems] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Action state
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const loadFeatures = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [featuresData, summaryData] = await Promise.all([
        getFeatures(),
        getFeatureSummary(),
      ]);
      setFeatures(featuresData);
      setSummary(summaryData);
    } catch (err) {
      console.error('[FeatureTracker] Failed to load:', err);
      setError('Failed to load features');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (id: string) => {
    try {
      const featureData = await getFeature(id);
      setDetail(featureData);
      // Load linked backlog items using server-side filter
      if (featureData.backlogItemIds.length > 0) {
        const linked = await getBacklogItems({ featureId: id });
        setLinkedItems(linked);
      } else {
        setLinkedItems([]);
      }
    } catch (err) {
      console.error('[FeatureTracker] Failed to load detail:', err);
      setDetail(null);
      setLinkedItems([]);
    }
  }, []);

  useEffect(() => { loadFeatures(); }, [loadFeatures]);

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
    else { setDetail(null); setLinkedItems([]); }
  }, [selectedId, loadDetail]);

  const handleSelect = (id: string) => {
    setSelectedId(prev => prev === id ? null : id);
    setActionError(null);
  };

  const handleRemoveItem = async (itemId: string) => {
    if (!selectedId) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await removeItemsFromFeature(selectedId, [itemId]);
      await loadDetail(selectedId);
      await loadFeatures();
    } catch (err) {
      setActionError('Failed to remove item');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    setActionLoading(true);
    try {
      await deleteFeature(selectedId);
      setSelectedId(null);
      setShowDeleteConfirm(false);
      await loadFeatures();
    } catch (err) {
      setActionError('Failed to delete feature');
    } finally {
      setActionLoading(false);
    }
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) return (
    <div className="p-6">
      <div className="bg-red-50 text-red-700 p-4 rounded-lg">{error}</div>
      <Button className="mt-4" onClick={loadFeatures}>Retry</Button>
    </div>
  );

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Feature Tracker</h1>
          <p className="text-sm text-gray-500 mt-1">Group and track related backlog items</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="secondary" onClick={loadFeatures}>Refresh</Button>
          <Button onClick={() => setShowCreate(true)}>+ Create Feature</Button>
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">{summary.total}</div>
            <div className="text-sm text-gray-500">Total Features</div>
          </Card>
          <Card className="p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{summary.byStatus?.active || 0}</div>
            <div className="text-sm text-gray-500">Active</div>
          </Card>
          <Card className="p-4 text-center">
            <div className="text-2xl font-bold text-gray-600">{summary.byStatus?.completed || 0}</div>
            <div className="text-sm text-gray-500">Completed</div>
          </Card>
          <Card className="p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{summary.totalItems}</div>
            <div className="text-sm text-gray-500">Linked Items</div>
          </Card>
        </div>
      )}

      {/* Action error */}
      {actionError && (
        <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg text-sm">
          {actionError}
          <button className="ml-2 underline" onClick={() => setActionError(null)}>dismiss</button>
        </div>
      )}

      {/* Main layout */}
      {features.length === 0 ? (
        <EmptyState
          title="No features yet"
          description="Create a feature to start grouping your backlog items."
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Feature list */}
          <div className="lg:col-span-1 space-y-3">
            {features.map(f => (
              <FeatureCard
                key={f.id}
                feature={f}
                isSelected={selectedId === f.id}
                onClick={() => handleSelect(f.id)}
              />
            ))}
          </div>

          {/* Right: Detail panel */}
          <div className="lg:col-span-2">
            {detail ? (
              <FeatureDetailPanel
                detail={detail}
                linkedItems={linkedItems}
                actionLoading={actionLoading}
                onEdit={() => setShowEdit(true)}
                onDelete={() => setShowDeleteConfirm(true)}
                onAddItems={() => setShowAddItems(true)}
                onRemoveItem={handleRemoveItem}
              />
            ) : (
              <Card className="p-8 text-center text-gray-400">
                Select a feature from the list to view details
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Create Feature Modal */}
      {showCreate && (
        <CreateFeatureModal
          onClose={() => setShowCreate(false)}
          onCreated={async () => {
            setShowCreate(false);
            await loadFeatures();
          }}
        />
      )}

      {/* Edit Feature Modal */}
      {showEdit && detail && (
        <EditFeatureModal
          feature={detail}
          onClose={() => setShowEdit(false)}
          onSaved={async () => {
            setShowEdit(false);
            if (selectedId) await loadDetail(selectedId);
            await loadFeatures();
          }}
        />
      )}

      {/* Add Items Modal */}
      {showAddItems && selectedId && (
        <AddItemsModal
          featureId={selectedId}
          existingItemIds={detail?.backlogItemIds || []}
          onClose={() => setShowAddItems(false)}
          onAdded={async () => {
            setShowAddItems(false);
            if (selectedId) await loadDetail(selectedId);
            await loadFeatures();
          }}
        />
      )}

      {/* Delete Confirmation */}
      <Modal isOpen={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} title="Delete Feature?">
        <p className="text-sm text-gray-600 mb-4">
          This will unlink all associated backlog items but will not delete them.
          This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete} disabled={actionLoading}>
            {actionLoading ? 'Deleting...' : 'Delete Feature'}
          </Button>
        </div>
      </Modal>
    </div>
  );
}

// ─── Sub-Components ──────────────────────────────────────────────

function FeatureCard({ feature, isSelected, onClick }: {
  feature: Feature;
  isSelected: boolean;
  onClick: () => void;
}) {
  const sc = statusConfig[feature.status] || statusConfig.planning;
  const pc = priorityConfig[feature.priority] || priorityConfig.medium;

  return (
    <div
      className={`cursor-pointer transition-all hover:shadow-md ${
        isSelected ? 'ring-2 ring-blue-500 border-blue-500 rounded-lg' : ''
      }`}
      onClick={onClick}
    >
    <Card className="p-4">
      <div className="flex items-start justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-900 truncate flex-1 mr-2">{feature.title}</h3>
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${sc.bg} ${sc.color}`}>
          {sc.label}
        </span>
      </div>
      <div className="flex items-center gap-2 mb-2">
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${pc.bg} ${pc.color}`}>
          {pc.label}
        </span>
        <span className="text-xs text-gray-500">{feature.backlogItemIds.length} items</span>
        {feature.project && (
          <span className="text-xs text-gray-400">{feature.project}</span>
        )}
      </div>
      <div className="text-[10px] text-gray-400 mt-1">{feature.backlogItemIds.length} item{feature.backlogItemIds.length !== 1 ? 's' : ''} linked</div>
    </Card>
    </div>
  );
}

function FeatureDetailPanel({ detail, linkedItems, actionLoading, onEdit, onDelete, onAddItems, onRemoveItem }: {
  detail: FeatureWithProgress;
  linkedItems: BacklogItem[];
  actionLoading: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onAddItems: () => void;
  onRemoveItem: (id: string) => void;
}) {
  const sc = statusConfig[detail.status] || statusConfig.planning;
  const pc = priorityConfig[detail.priority] || priorityConfig.medium;

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-mono text-gray-400">{detail.id}</span>
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${sc.bg} ${sc.color}`}>
                {sc.label}
              </span>
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${pc.bg} ${pc.color}`}>
                {pc.label}
              </span>
            </div>
            <h2 className="text-lg font-bold text-gray-900">{detail.title}</h2>
            {detail.description && (
              <p className="text-sm text-gray-600 mt-1">{detail.description}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={onEdit}>Edit</Button>
            <Button variant="danger" size="sm" onClick={onDelete}>Delete</Button>
          </div>
        </div>

        {/* Metadata */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div>
            <span className="text-gray-500">Project:</span>
            <span className="ml-1 font-medium text-gray-700">{detail.project}</span>
          </div>
          <div>
            <span className="text-gray-500">Owner:</span>
            <span className="ml-1 font-medium text-gray-700">{detail.owner || 'Unassigned'}</span>
          </div>
          <div>
            <span className="text-gray-500">Target:</span>
            <span className="ml-1 font-medium text-gray-700">
              {detail.targetDate ? new Date(detail.targetDate).toLocaleDateString() : 'None'}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Created:</span>
            <span className="ml-1 font-medium text-gray-700">
              {new Date(detail.createdAt).toLocaleDateString()}
            </span>
          </div>
        </div>

        {/* Labels */}
        {detail.labels.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {detail.labels.map((label, i) => (
              <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
                {label}
              </span>
            ))}
          </div>
        )}
      </Card>

      {/* Progress */}
      <Card className="p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Progress</h3>
        <div className="flex items-center gap-4 mb-3">
          <div className="flex-1">
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-green-500 h-3 rounded-full transition-all"
                style={{ width: `${detail.progress.pct}%` }}
              />
            </div>
          </div>
          <span className="text-sm font-bold text-gray-700 whitespace-nowrap">
            {detail.progress.done}/{detail.progress.total} ({detail.progress.pct}%)
          </span>
        </div>
        {detail.progress.total > 0 && (
          <div className="flex flex-wrap gap-3">
            {Object.entries(detail.progress.byStatus).map(([status, count]) => (
              <div key={status} className="flex items-center gap-1.5">
                <div className={`w-2.5 h-2.5 rounded-full ${itemStatusColors[status] || 'bg-gray-300'}`} />
                <span className="text-xs text-gray-600">{status.replace('_', ' ')}: {count}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Linked Items */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700">
            Linked Backlog Items ({linkedItems.length})
          </h3>
          <Button size="sm" onClick={onAddItems}>+ Add Items</Button>
        </div>
        {linkedItems.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">No items linked yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left">
                  <th className="pb-2 text-xs font-medium text-gray-500 uppercase">ID</th>
                  <th className="pb-2 text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="pb-2 text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="pb-2 text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="pb-2 text-xs font-medium text-gray-500 uppercase">Priority</th>
                  <th className="pb-2 text-xs font-medium text-gray-500 uppercase">Agent</th>
                  <th className="pb-2 text-xs font-medium text-gray-500 uppercase"></th>
                </tr>
              </thead>
              <tbody>
                {linkedItems.map(item => {
                  const isc = backlogStatusConfig[item.status] || { label: item.status, color: 'text-gray-600', bg: 'bg-gray-100' };
                  return (
                    <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2 font-mono text-xs text-gray-400">{item.id}</td>
                      <td className="py-2 text-gray-900 max-w-[200px] truncate">{item.title}</td>
                      <td className="py-2">
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          item.type === 'defect' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {item.type === 'defect' ? 'Defect' : 'Story'}
                        </span>
                      </td>
                      <td className="py-2">
                        <span className={`text-xs px-1.5 py-0.5 rounded ${isc.bg} ${isc.color}`}>
                          {item.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="py-2 text-xs text-gray-600">{item.priority}</td>
                      <td className="py-2 text-xs text-gray-600">{item.assignedAgent || '—'}</td>
                      <td className="py-2">
                        <button
                          className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
                          onClick={() => onRemoveItem(item.id)}
                          disabled={actionLoading}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── Modals ──────────────────────────────────────────────────────

function CreateFeatureModal({ onClose, onCreated }: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [project, setProject] = useState('rink_rater');
  const [priority, setPriority] = useState('medium');
  const [owner, setOwner] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [labelsStr, setLabelsStr] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) { setError('Title is required'); return; }
    setSaving(true);
    setError(null);
    try {
      await createFeature({
        title: title.trim(),
        description: description.trim(),
        project,
        priority,
        owner: owner || null,
        targetDate: targetDate || null,
        labels: labelsStr ? labelsStr.split(',').map(l => l.trim()).filter(Boolean) : [],
      });
      onCreated();
    } catch (err) {
      setError('Failed to create feature');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Create Feature" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <div className="text-sm text-red-600 bg-red-50 p-2 rounded">{error}</div>}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Feature title"
            autoFocus
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            rows={3}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Brief description of this feature"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project *</label>
            <select
              value={project}
              onChange={e => setProject(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="rink_rater">Rink Rater</option>
              <option value="aidlc_admin">AIDLC Admin</option>
              <option value="ldaja_website">LDAJA Website</option>
              <option value="ldaja_infrastructure">LDAJA Infrastructure</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
            <select
              value={priority}
              onChange={e => setPriority(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Owner (Agent ID)</label>
            <input
              type="text"
              value={owner}
              onChange={e => setOwner(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g. marcus"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Target Date</label>
            <input
              type="date"
              value={targetDate}
              onChange={e => setTargetDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Labels (comma-separated)</label>
          <input
            type="text"
            value={labelsStr}
            onChange={e => setLabelsStr(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="e.g. authentication, phase-1"
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving}>
            {saving ? 'Creating...' : 'Create Feature'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function EditFeatureModal({ feature, onClose, onSaved }: {
  feature: Feature;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(feature.title);
  const [description, setDescription] = useState(feature.description);
  const [status, setStatus] = useState(feature.status);
  const [priority, setPriority] = useState(feature.priority);
  const [owner, setOwner] = useState(feature.owner || '');
  const [targetDate, setTargetDate] = useState(feature.targetDate || '');
  const [labelsStr, setLabelsStr] = useState(feature.labels.join(', '));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) { setError('Title is required'); return; }
    setSaving(true);
    setError(null);
    try {
      await updateFeature(feature.id, {
        title: title.trim(),
        description: description.trim(),
        status,
        priority,
        owner: owner || null,
        targetDate: targetDate || null,
        labels: labelsStr ? labelsStr.split(',').map(l => l.trim()).filter(Boolean) : [],
      });
      onSaved();
    } catch (err) {
      setError('Failed to update feature');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Edit Feature" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">

        {error && <div className="text-sm text-red-600 bg-red-50 p-2 rounded">{error}</div>}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            rows={3}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={status}
              onChange={e => setStatus(e.target.value as Feature['status'])}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="planning">Planning</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
            <select
              value={priority}
              onChange={e => setPriority(e.target.value as Feature['priority'])}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Owner (Agent ID)</label>
            <input
              type="text"
              value={owner}
              onChange={e => setOwner(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g. marcus"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Target Date</label>
            <input
              type="date"
              value={targetDate}
              onChange={e => setTargetDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Labels (comma-separated)</label>
          <input
            type="text"
            value={labelsStr}
            onChange={e => setLabelsStr(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function AddItemsModal({ featureId, existingItemIds, onClose, onAdded }: {
  featureId: string;
  existingItemIds: string[];
  onClose: () => void;
  onAdded: () => void;
}) {
  const [availableItems, setAvailableItems] = useState<BacklogItem[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const allItems = await getBacklogItems();
        // Show items not already linked to this feature
        const available = allItems.filter(item => !existingItemIds.includes(item.id));
        setAvailableItems(available);
      } catch (err) {
        setError('Failed to load backlog items');
      } finally {
        setLoading(false);
      }
    })();
  }, [existingItemIds]);

  const filtered = search
    ? availableItems.filter(item =>
        item.title.toLowerCase().includes(search.toLowerCase()) ||
        item.id.toLowerCase().includes(search.toLowerCase())
      )
    : availableItems;

  const toggleItem = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAdd = async () => {
    if (selectedIds.size === 0) return;
    setSaving(true);
    setError(null);
    try {
      await addItemsToFeature(featureId, Array.from(selectedIds));
      onAdded();
    } catch (err) {
      setError('Failed to add items');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Add Backlog Items" size="lg">
      <div className="space-y-4">

        {error && <div className="text-sm text-red-600 bg-red-50 p-2 rounded">{error}</div>}

        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Search by title or ID..."
        />

        {loading ? (
          <div className="text-center py-8 text-sm text-gray-400">Loading items...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-8 text-sm text-gray-400">No available items found</div>
        ) : (
          <div className="max-h-[300px] overflow-y-auto border border-gray-200 rounded-lg">
            {filtered.slice(0, 50).map(item => (
              <label
                key={item.id}
                className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
              >
                <input
                  type="checkbox"
                  checked={selectedIds.has(item.id)}
                  onChange={() => toggleItem(item.id)}
                  className="rounded border-gray-300"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-900 truncate">{item.title}</div>
                  <div className="text-xs text-gray-400">
                    {item.id} &middot; {item.type === 'defect' ? 'Defect' : 'Story'} &middot; {item.status.replace('_', ' ')} &middot; {item.priority}
                  </div>
                </div>
              </label>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between pt-2">
          <span className="text-sm text-gray-500">
            {selectedIds.size} item{selectedIds.size !== 1 ? 's' : ''} selected
          </span>
          <div className="flex gap-3">
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button onClick={handleAdd} disabled={saving || selectedIds.size === 0}>
              {saving ? 'Adding...' : `Add ${selectedIds.size} Item${selectedIds.size !== 1 ? 's' : ''}`}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
